import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";
import "./styles.css";

const CreateViewModal = ({
  open,
  onClose,
  onSave,
  isTransactionPage = false,
  existingViews = [],
  initialName = "",
  initialShareWithOther = false,
  initialRestricted = false,
  initialSetAsDefault = false,
}) => {
  const [viewName, setViewName] = useState("");
  const [shareWithOther, setShareWithOther] = useState(initialShareWithOther);
  const [restricted, setRestricted] = useState(initialRestricted);
  const [setAsDefault, setSetAsDefault] = useState(initialSetAsDefault);
  const [viewNameFieldActivated, setViewNameFieldActivated] = useState(false);

  // Check if user has no owned views (not just no views at all)
  const hasNoOwnedViews = useMemo(() => {
    return !existingViews.some(v => v.isOwner === true);
  }, [existingViews]);
  
  const isViewNameReadOnly = hasNoOwnedViews && !viewNameFieldActivated;

  // Reset form when modal opens/closes; when no owned views, force Set as Default checked
  useEffect(() => {
    if (open) {
      setViewName("");
      setShareWithOther(initialShareWithOther);
      setRestricted(initialRestricted);
      setSetAsDefault(hasNoOwnedViews ? true : initialSetAsDefault);
      setViewNameFieldActivated(false);
    }
  }, [open, hasNoOwnedViews, initialShareWithOther, initialRestricted, initialSetAsDefault]);

  // Handle share with others change - reset restricted if unchecked
  const handleShareWithOtherChange = (event) => {
    const checked = event.target.checked;
    setShareWithOther(checked);
    if (!checked) {
      setRestricted(false);
    }
  };

  // Validation logic; no owned views: Save only enabled after user has clicked into view name (first time)
  const validation = useMemo(() => {
    if (hasNoOwnedViews && !viewNameFieldActivated) {
      return { error: null, saveDisabled: true };
    }
    const trimmed = viewName.trim();
    // Require view name for all pages
    if (!trimmed) {
      return { error: "", saveDisabled: true };
    }
    // Check for duplicate view names across all pages
    const duplicate = existingViews.find(
      (v) => v.viewName && v.viewName.toLowerCase() === trimmed.toLowerCase() && v.isOwner === true
    );
    if (duplicate) {
      return {
        error: "This label already exists. Please try again.",
        saveDisabled: true,
      };
    }
    return { error: null, saveDisabled: false };
  }, [viewName, existingViews, hasNoOwnedViews, viewNameFieldActivated]);

  // Close (X) button: clear all values and close modal
  const handleCloseClick = () => {
    setViewName("");
    setShareWithOther(initialShareWithOther);
    setRestricted(initialRestricted);
    setSetAsDefault(hasNoOwnedViews ? true : initialSetAsDefault);
    onClose();
  };

  // Handle save (when no owned views, always pass setAsDefault true so first view is default)
  const handleSave = () => {
    onSave?.({
      name: viewName.trim(),
      shareWithOther,
      restricted,
      setAsDefault: hasNoOwnedViews ? true : setAsDefault,
    });
  };

  return (
    <Dialog open={open} onClose={handleCloseClick} className="create-view-modal">
      <DialogTitle className="create-view-modal-title">
        <span>Add custom view</span>
        <IconButton
          className="create-view-modal-close-btn"
          size="small"
          onClick={handleCloseClick}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box className="create-view-modal-content">
        <Typography className="create-view-modal-description" variant="body2" color="textSecondary">
          Create your custom view with filters and columns applied.
        </Typography>
        
        <Box className="create-view-modal-view-name-section">
          <Typography className="create-view-modal-view-name-label" variant="body2">
            View name<span className="required-asterisk"> *</span>
          </Typography>
          <TextField
            className="create-view-modal-view-name"
            fullWidth
            size="small"
            value={hasNoOwnedViews && !viewNameFieldActivated ? "" : viewName}
            onChange={(e) => setViewName(e.target.value)}
            onFocus={() => hasNoOwnedViews && setViewNameFieldActivated(true)}
            placeholder="Enter View name"
            variant="outlined"
            error={!!validation.error}
            inputProps={{ readOnly: isViewNameReadOnly }}
            InputProps={{
              endAdornment: viewName && (!hasNoOwnedViews || viewNameFieldActivated) ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setViewName("")}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label="Clear view name"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {validation.error && (
            <Box className="create-view-modal-error-message">
              <WarningAmberIcon className="create-view-modal-error-icon" fontSize="small" />
              <Typography className="create-view-modal-error-text">
                {validation.error}
              </Typography>
            </Box>
          )}
        </Box>
        <Box className="create-view-modal-checkboxes-row">
          <FormControlLabel
            className="create-view-modal-share-label"
            control={
              <Checkbox
                checked={shareWithOther}
                onChange={handleShareWithOtherChange}
                color="primary"
                size="small"
              />
            }
            label="Share with others"
          />
          <CustomTooltip
            title="You can share the view with everyone in your account."
            arrow
            placement="top"
            maxWidth="206px"
          >
            <IconButton className="create-view-modal-info-tooltip create-view-modal-info-icon-btn" size="small" aria-label="Share with others info">
              <InfoOutlinedIcon />
            </IconButton>
          </CustomTooltip>
          {shareWithOther && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={restricted}
                    onChange={(e) => setRestricted(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Restricted"
              />
              <CustomTooltip
                title="If restricted, only you can modify this view. If unrestricted, anyone with view access can edit the view."
                arrow
                placement="top"
                maxWidth="227px"
              >
                <IconButton className="create-view-modal-info-icon-btn" size="small" aria-label="Restricted info">
                  <InfoOutlinedIcon />
                </IconButton>
              </CustomTooltip>
            </>
          )}
        </Box>
        <Box className="create-view-modal-set-default-row">
          <CustomTooltip
            title={hasNoOwnedViews ? "Your first view will be set as default." : ""}
            arrow
            placement="top"
          >
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={setAsDefault}
                    onChange={(e) => setSetAsDefault(e.target.checked)}
                    disabled={hasNoOwnedViews}
                    color="primary"
                    size="small"
                  />
                }
                label="Set as default"
              />
            </span>
          </CustomTooltip>
        </Box>
      </Box>
      <DialogActions className="create-view-modal-actions">
        <Button onClick={onClose} className="create-view-modal-cancel-btn">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={validation.saveDisabled}
          className="create-view-modal-save-btn"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(CreateViewModal);
